/**
 * IndiaRecessionDashboard.js
 * ==========================
 * India Recession Probability Dashboard using Indian economic indicators
 * Similar to US RecessionDashboard but with RBI, MOSPI data
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, RefreshCw, AlertTriangle,
  Activity, Info, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const IndiaRecessionDashboard = () => {
  const [indicators, setIndicators] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIndicator, setExpandedIndicator] = useState(null);

  // India recession indicator weights and thresholds
  const INDICATOR_CONFIG = {
    'RBI Repo Rate': {
      weight: 15,
      recessionary: (v) => v >= 7.0,
      cautionary: (v) => v >= 6.5,
      description: 'High rates slow borrowing and economic activity',
      implications: {
        high: 'Tight monetary policy restricts credit growth, impacting consumption and investment.',
        normal: 'Accommodative rates support economic growth and lending activity.'
      }
    },
    'India CPI Inflation': {
      weight: 20,
      recessionary: (v) => v >= 7.0,
      cautionary: (v) => v >= 5.5,
      description: 'High inflation erodes purchasing power and forces RBI to hike rates',
      implications: {
        high: 'Elevated inflation squeezes household budgets and may trigger rate hikes.',
        normal: 'Stable inflation allows RBI to maintain supportive monetary policy.'
      }
    },
    'India GDP Growth': {
      weight: 25,
      recessionary: (v) => v < 4.0,
      cautionary: (v) => v < 5.5,
      description: 'GDP growth below 5% signals economic weakness',
      implications: {
        high: 'Strong growth supports corporate earnings and employment.',
        low: 'Slowing growth may lead to job losses and reduced investment.'
      },
      inverted: true
    },
    '10-Year G-Sec Yield': {
      weight: 10,
      recessionary: (v) => v >= 8.0,
      cautionary: (v) => v >= 7.5,
      description: 'Rising yields increase borrowing costs for government and corporates',
      implications: {
        high: 'High yields attract FII debt flows but raise corporate borrowing costs.',
        normal: 'Stable yields support equity valuations and corporate capex.'
      }
    },
    'Manufacturing PMI': {
      weight: 15,
      recessionary: (v) => v < 48,
      cautionary: (v) => v < 50,
      description: 'PMI below 50 indicates manufacturing contraction',
      implications: {
        high: 'Expanding manufacturing supports industrial production and jobs.',
        low: 'Contracting PMI signals weak demand and potential layoffs.'
      },
      inverted: true
    },
    'Services PMI': {
      weight: 15,
      recessionary: (v) => v < 48,
      cautionary: (v) => v < 50,
      description: 'Services account for 55%+ of GDP; contraction is concerning',
      implications: {
        high: 'Strong services sector drives consumption and employment.',
        low: 'Weak services PMI signals broad economic weakness.'
      },
      inverted: true
    }
  };

  const fetchIndicators = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/india/indicators`);
      if (res.ok) {
        const data = await res.json();
        setIndicators(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIndicators();
  }, [fetchIndicators]);

  // Calculate recession probability
  const calculateProbability = () => {
    if (!indicators) return { probability: 0, signals: [] };

    let totalWeight = 0;
    let recessSignal = 0;
    const signals = [];

    Object.entries(INDICATOR_CONFIG).forEach(([name, config]) => {
      const indicator = indicators[name];
      if (!indicator) return;

      totalWeight += config.weight;
      const value = indicator.value;
      
      let status = 'normal';
      let contribution = 0;

      if (config.inverted) {
        if (config.recessionary(value)) {
          status = 'recessionary';
          contribution = config.weight;
        } else if (config.cautionary(value)) {
          status = 'cautionary';
          contribution = config.weight * 0.5;
        }
      } else {
        if (config.recessionary(value)) {
          status = 'recessionary';
          contribution = config.weight;
        } else if (config.cautionary(value)) {
          status = 'cautionary';
          contribution = config.weight * 0.5;
        }
      }

      recessSignal += contribution;
      signals.push({ name, value, status, weight: config.weight, contribution });
    });

    const probability = totalWeight > 0 ? (recessSignal / totalWeight) * 100 : 0;
    return { probability: Math.min(probability, 100), signals };
  };

  const { probability, signals } = calculateProbability();

  const getProbabilityColor = (p) => {
    if (p >= 60) return '#ef4444';
    if (p >= 40) return '#f59e0b';
    if (p >= 20) return '#eab308';
    return '#22c55e';
  };

  const getProbabilityLabel = (p) => {
    if (p >= 60) return 'High Risk';
    if (p >= 40) return 'Elevated';
    if (p >= 20) return 'Moderate';
    return 'Low';
  };

  const getStatusColor = (status) => {
    if (status === 'recessionary') return '#ef4444';
    if (status === 'cautionary') return '#f59e0b';
    return '#22c55e';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', color: '#94a3b8' }}>
        <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginRight: '12px' }} />
        Loading India Economic Data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
        <AlertTriangle size={40} style={{ marginBottom: '12px' }} />
        <p>Failed to load data: {error}</p>
        <button onClick={fetchIndicators} style={{ padding: '8px 16px', background: '#3b82f6', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <span style={{ fontSize: '28px' }}>🇮🇳</span>
          <h2 style={{ margin: 0, color: '#f1f5f9', fontSize: '20px', fontWeight: '700' }}>
            India Economic Outlook
          </h2>
          <button
            onClick={fetchIndicators}
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', background: '#3b82f6', border: 'none',
              borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '13px'
            }}
          >
            <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>
        <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
          Multi-factor analysis of India's economic health using RBI, MOSPI, and S&P Global data
        </p>
      </div>

      {/* Main Probability Card */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b, #0f172a)',
        borderRadius: '16px',
        padding: '32px',
        marginBottom: '24px',
        border: '1px solid #334155',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '16px', color: '#94a3b8', fontSize: '14px', fontWeight: '500' }}>
          ECONOMIC SLOWDOWN PROBABILITY
        </div>
        
        {/* Gauge */}
        <div style={{ position: 'relative', width: '200px', height: '100px', margin: '0 auto 16px' }}>
          <svg viewBox="0 0 200 100" style={{ width: '100%', height: '100%' }}>
            {/* Background arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#334155"
              strokeWidth="16"
              strokeLinecap="round"
            />
            {/* Colored arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke={getProbabilityColor(probability)}
              strokeWidth="16"
              strokeLinecap="round"
              strokeDasharray={`${probability * 2.51} 251`}
            />
          </svg>
          <div style={{
            position: 'absolute',
            bottom: '0',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '42px', fontWeight: '700', color: getProbabilityColor(probability) }}>
              {probability.toFixed(0)}%
            </div>
          </div>
        </div>

        <div style={{
          display: 'inline-block',
          padding: '8px 20px',
          borderRadius: '20px',
          background: `${getProbabilityColor(probability)}20`,
          color: getProbabilityColor(probability),
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {getProbabilityLabel(probability)} Risk
        </div>

        <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '16px', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
          {probability >= 40 
            ? 'Multiple indicators signal economic stress. Consider defensive positioning in portfolio.'
            : 'Economic indicators are largely stable. Growth-oriented investments may be appropriate.'}
        </p>
      </div>

      {/* Signal Breakdown */}
      <div style={{
        background: '#1e293b',
        borderRadius: '12px',
        border: '1px solid #334155',
        overflow: 'hidden'
      }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Activity size={18} color="#60a5fa" />
          <span style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>Indicator Breakdown</span>
        </div>

        {signals.map((signal, i) => {
          const config = INDICATOR_CONFIG[signal.name];
          const isExpanded = expandedIndicator === signal.name;
          const indicator = indicators[signal.name];
          
          return (
            <div key={i} style={{ borderBottom: i < signals.length - 1 ? '1px solid #334155' : 'none' }}>
              <div
                onClick={() => setExpandedIndicator(isExpanded ? null : signal.name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#273548'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {/* Status indicator */}
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: getStatusColor(signal.status),
                  marginRight: '12px'
                }} />

                {/* Name & Value */}
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '500' }}>
                    {signal.name}
                  </div>
                  <div style={{ color: '#64748b', fontSize: '12px' }}>
                    Weight: {signal.weight}%
                  </div>
                </div>

                {/* Current Value */}
                <div style={{ textAlign: 'right', marginRight: '12px' }}>
                  <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600' }}>
                    {indicator?.unit === '%' ? `${signal.value.toFixed(2)}%` :
                     indicator?.unit === '$B' ? `$${signal.value.toFixed(0)}B` :
                     signal.value.toFixed(2)}
                  </div>
                  <div style={{ color: getStatusColor(signal.status), fontSize: '11px', fontWeight: '600' }}>
                    {signal.status.toUpperCase()}
                  </div>
                </div>

                {/* Expand icon */}
                {isExpanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{
                  padding: '0 16px 16px 38px',
                  background: 'rgba(0,0,0,0.2)'
                }}>
                  <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 12px' }}>
                    {config.description}
                  </p>
                  <div style={{
                    background: 'rgba(96, 165, 250, 0.1)',
                    borderRadius: '8px',
                    padding: '12px',
                    borderLeft: '3px solid #60a5fa'
                  }}>
                    <div style={{ color: '#60a5fa', fontSize: '11px', fontWeight: '600', marginBottom: '4px' }}>
                      MARKET IMPLICATION
                    </div>
                    <p style={{ color: '#cbd5e1', fontSize: '13px', margin: 0 }}>
                      {signal.status === 'recessionary' || signal.status === 'cautionary'
                        ? (config.implications.high || config.implications.low)
                        : (config.implications.normal || config.implications.high)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: 'rgba(251, 191, 36, 0.1)',
        borderRadius: '8px',
        border: '1px solid rgba(251, 191, 36, 0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
          <Info size={18} color="#fbbf24" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ color: '#fbbf24', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
              Disclaimer
            </div>
            <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0, lineHeight: '1.5' }}>
              This is a simplified model based on publicly available data. India's economy has different dynamics 
              than developed markets - it has rarely experienced technical recessions due to structural growth drivers. 
              "Slowdown" is a more appropriate term. Always consult professional advisors for investment decisions.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default IndiaRecessionDashboard;
